// Angular imports
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

// 3rd party imports
import * as moment from 'moment-timezone';

// CC-Common imports
import { CCLocalStorageCategory, CCLocalStorageService, IconConstants } from 'cc-common';

// Service imports
import { HelperService } from '../../_services/shared-services/helper.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { PortalAnnouncementService } from '../../_services/data-services/portal-announcement.service';

// Model imports
import { AnnouncementsResultDto } from '../../_models/announcements-result-dto';
import { AnnouncementIdParametersDto } from '../../_models/announcement-id-parameters-dto';
import { AnnouncementAccessResultDto } from '../../_models/announcement-access-result-dto';

// Constant imports
import { StringFormatConstants } from '../../_constants/string-format-constants';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';

@Component({
    selector: 'app-portal-announcement',
    templateUrl: './portal-announcement.component.html',
    styleUrls: ['./portal-announcement.component.scss']
})
export class PortalAnnouncementComponent implements OnInit {
    @Input() visible = false;
    @Input() isResidentAnnouncement = false;
    @Output() onClose = new EventEmitter<void>();
    @Output() onRead = new EventEmitter<void>();

    tabItems = [];
    tabIndex = 0;
    iconAdd = IconConstants.ADD;
    iconRefresh = IconConstants.REFRESH;
    iconCheck = IconConstants.CHECK;
    iconEdit = IconConstants.EDIT;
    iconDelete = IconConstants.DELETE;

    loadingVisible = true;
    showAnnouncements = true;
    isSmallScreen = false;
    popupWidth = '70%';
    popupHeight = '90%';
    multiIconButtonOptions = '';
    announcements: AnnouncementsResultDto[] = [];

    expiredFromDate: Date;
    expiredToDate: Date;
    announcementId: number;

    announcementAccess: AnnouncementAccessResultDto;
    showAddNewAnnouncement = false;

    constructor(
        private readonly screenService: ScreenService,
        private readonly helperService: HelperService,
        private readonly annoucementService: PortalAnnouncementService,
        private readonly errorService: ServerErrorService,
        private readonly localStorageService: CCLocalStorageService
    ) {
        this.onForceRefresh = this.onForceRefresh.bind(this);
        this.onAddNewClick = this.onAddNewClick.bind(this);
    }

    ngOnInit() {
        this.updateScreenSize();
        this.screenService.changed.subscribe(() => this.updateScreenSize());
        if (this.isResidentAnnouncement) {
            this.tabItems = [
                { text: 'Active Announcements', id: 'active', announcements: [] },
                { text: 'Expired Announcements', id: 'expired', announcements: [] }
            ];
        } else {
            this.tabItems = [
                { text: 'Unread Announcements', id: 'unread', announcements: [] },
                { text: 'Active Announcements', id: 'active', announcements: [] },
                { text: 'Expired Announcements', id: 'expired', announcements: [] },
                { text: 'Future Announcements', id: 'future', announcements: [] }
            ];
        }
        this.multiIconButtonOptions = JSON.stringify({
            beforeIcons: [IconConstants.ADD],
            label: 'Add New Announcement'
        });
        this.showAnnouncements =
            (this.localStorageService.getItem(
                StorageKeyConstants.userShowAnnouncementsKey,
                CCLocalStorageCategory.PREF
            ) || 'true') === 'true';
        this.expiredToDate = moment().toDate();
        this.expiredFromDate = moment(this.expiredToDate).subtract(6, 'months').toDate();
        this.show();
    }

    show(): void {
        this.visible = true;
        this.populateAnnouncementsAccess();
        this.populatePortalAnnouncements();
    }

    hide(): void {
        this.onClose.emit();
        this.visible = false;
    }

    repaint(e: any): void {
        e.component.repaint();
    }

    onTabChanged(e: any): void {
        this.populatePortalAnnouncements();
    }

    onForceRefresh(): void {
        this.populatePortalAnnouncements();
    }

    getFormatedDate(releaseDate: any) {
        const userTimezone = this.helperService.getUserTimezone();
        return moment.tz(releaseDate, userTimezone).format(StringFormatConstants.dateFormat.toUpperCase());
    }

    saveShowAnnouncements(e: any): void {
        this.localStorageService.setItem(
            StorageKeyConstants.userShowAnnouncementsKey,
            this.showAnnouncements ? 'true' : 'false',
            CCLocalStorageCategory.PREF
        );
    }

    isUnreadAnnouncement(announcement: AnnouncementsResultDto): boolean {
        return (
            (this.tabIndex === 0 || this.tabIndex === 1) &&
            this.tabItems[0].announcements.findIndex((a) => a.announcementId === announcement.announcementId) !== -1
        );
    }

    onAddNewClick(): void {
        this.showAddNewAnnouncement = true;
    }

    onCloseEditor(): void {
        this.announcementId = null;
        this.showAddNewAnnouncement = false;
    }

    onMarkAsReadClick(announcement: AnnouncementsResultDto): void {
        if (!!announcement) {
            const param: AnnouncementIdParametersDto = new AnnouncementIdParametersDto();
            param.announcementId = announcement.announcementId;
            this.annoucementService.markAsReadAnnouncements(param).subscribe(
                () => {
                    this.onRead.emit();
                    this.onRefresh();
                },
                (error) => {
                    this.errorService.displayError(error);
                }
            );
        }
    }

    onEditClick(announcement: AnnouncementsResultDto): void {
        this.announcementId = announcement.announcementId;
        this.showAddNewAnnouncement = true;
    }

    onDeleteClick(announcement: AnnouncementsResultDto): void {
        const dialog = this.helperService.getConfirmationDialog(
            'Confirmation',
            'Are you sure you want to delete this announcement for ALL users?',
            'Yes',
            'No',
            'danger',
            'default'
        );
        dialog.show().then((ans: boolean) => {
            if (ans) {
                this.deletePortalAnnouncement(announcement.announcementId);
            }
        });
    }

    onRefresh(): void {
        if (this.tabIndex > 0) {
            this.refreshUnreadAnnouncements();
        } else {
            this.populatePortalAnnouncements();
        }
        this.onRead.emit();
    }

    private populatePortalAnnouncements(): void {
        const onSuccess = (announcements) => {
            this.tabItems[this.tabIndex].announcements = announcements;
            this.announcements = announcements;
            this.loadingVisible = false;
        };
        const onError = (error) => {
            this.errorService.displayError(error);
            this.loadingVisible = false;
        };

        this.loadingVisible = true;
        const category = this.tabItems[this.tabIndex].id;
        if (category === 'expired') {
            this.annoucementService
                .getExpiredAnnouncements(this.expiredFromDate, this.expiredToDate)
                .subscribe(onSuccess, onError);
        } else {
            this.annoucementService.getAnnouncements(category).subscribe(onSuccess, onError);
        }
    }

    private refreshUnreadAnnouncements(): void {
        this.annoucementService.getAnnouncements('unread').subscribe(
            (announcements) => {
                this.tabItems[0].announcements = announcements;
                this.populatePortalAnnouncements();
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private deletePortalAnnouncement(announcementId: number): void {
        this.loadingVisible = true;
        const params = new AnnouncementIdParametersDto(announcementId);
        this.annoucementService.deleteAnnouncement(params).subscribe(
            () => {
                this.onRefresh();
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateAnnouncementsAccess(): void {
        this.annoucementService.getAnnouncementAccess().subscribe(
            (access: AnnouncementAccessResultDto) => {
                this.announcementAccess = access;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        if (this.isSmallScreen) {
            this.popupWidth = '95%';
        } else {
            this.popupWidth = '70%';
        }
    }
}
